import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import {
  CURRENCY_LABEL,
  STORE_CATALOG,
  STORE_TOOL_ORDER,
  storeSubGroupsForTool,
  grantContentLabels,
  type StoreCatalogItem,
  type StoreItemId,
  type ToolId,
} from "../../game/store-catalog.js";
import { TOOL_ICONS, TOOL_LABELS } from "./tools.js";
import type { EquippedResultCardTheme } from "../../game/types.js";
import { lookPreviewStyle } from "./CreativeOptionCard.js";
import { creativeOptionLabel } from "../lib/store-labels.js";
import { SHARED_LOOK_TREATMENTS } from "../../content/creative-catalogs.js";
import { track } from "../../game/analytics.js";

export type StudioStoreDrawerProps = {
  open: boolean;
  onClose: () => void;
  cash: number;
  purchases: readonly StoreItemId[];
  equippedTheme: EquippedResultCardTheme;
  focusItemId?: StoreItemId | null;
  /** Tool category to open on (Clips / Focus / … / Sticker). */
  focusTool?: ToolId | null;
  returnFocusRef: RefObject<HTMLElement | null>;
  /** Stable focus target if returnFocusRef disconnects (locked→owned). */
  returnFocusSelector?: string | null;
  onPurchase: (itemId: StoreItemId) => void;
  onEquipTheme: (theme: EquippedResultCardTheme) => void;
  /** When true, hide Buy (challenge freeze). */
  purchasesDisabled?: boolean;
};

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function toolForItem(id: StoreItemId | null | undefined): ToolId | null {
  if (!id) return null;
  return STORE_CATALOG.find((i) => i.id === id)?.tool ?? null;
}

/**
 * In-editor Studio Bag store. Renders as an in-device overlay INSIDE the phone
 * frame (same pattern as "How it's landing"), never a top-layer modal that
 * escapes the bezel. Organized by edit tool (Clips / Focus / … / Sticker), each
 * tab reusing the editor dock icon. Hierarchy: category → sub-category → item
 * card → item detail (icon, price in Bits, plain gameplay effect, Buy / Owned).
 */
export function StudioStoreDrawer(props: StudioStoreDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const [tool, setTool] = useState<ToolId>(
    props.focusTool ?? toolForItem(props.focusItemId) ?? "look",
  );
  const [selectedId, setSelectedId] = useState<StoreItemId | null>(
    props.focusItemId ?? null,
  );

  useEffect(() => {
    if (!props.open) return;
    const nextTool = props.focusTool ?? toolForItem(props.focusItemId);
    if (nextTool) setTool(nextTool);
    setSelectedId(props.focusItemId ?? null);
  }, [props.open, props.focusItemId, props.focusTool]);

  // Focus the close button on open; restore focus to the opener on close.
  useEffect(() => {
    if (props.open) {
      wasOpen.current = true;
      closeRef.current?.focus();
    } else if (wasOpen.current) {
      wasOpen.current = false;
      queueMicrotask(() => {
        const tryFocus = (el: HTMLElement | null | undefined) => {
          if (!el || typeof el.focus !== "function" || !document.contains(el)) {
            return false;
          }
          el.focus();
          return document.activeElement === el;
        };
        if (tryFocus(props.returnFocusRef.current)) return;
        if (
          props.returnFocusSelector &&
          tryFocus(document.querySelector<HTMLElement>(props.returnFocusSelector))
        ) {
          return;
        }
        tryFocus(document.querySelector<HTMLElement>('[data-testid="studio-bag-btn"]'));
      });
    }
  }, [props.open, props.returnFocusRef, props.returnFocusSelector]);

  // Bring the active category tab into view when the sheet opens.
  useEffect(() => {
    if (!props.open) return;
    const el = activeTabRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", inline: "center" });
    }
  }, [props.open, tool]);

  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        props.onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props.open, props]);

  const groups = useMemo(() => storeSubGroupsForTool(tool), [tool]);
  const selected: StoreCatalogItem | null = useMemo(
    () => (selectedId ? STORE_CATALOG.find((i) => i.id === selectedId) ?? null : null),
    [selectedId],
  );

  if (!props.open) return null;

  const selectTool = (next: ToolId) => {
    setTool(next);
    setSelectedId(null);
  };

  return (
    <div
      className="studio-store-overlay"
      data-testid="studio-store-drawer"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div
        className="studio-store-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="studio-store-title"
      >
        <header className="studio-store-head">
          <h2 id="studio-store-title">Studio Bag</h2>
          <p className="bits-badge" data-testid="studio-store-cash">
            {CURRENCY_LABEL} <strong>{props.cash}</strong>
          </p>
          <button
            type="button"
            ref={closeRef}
            className="studio-store-close"
            data-testid="close-studio-store"
            aria-label="Close Studio Bag"
            onClick={() => props.onClose()}
          >
            ✕
          </button>
        </header>

        {/* Category row REUSES the editor tool-dock's button styling
            (tool-dock-row / tool-dock-card / tool-dock-icon / tool-dock-label)
            so the store's Clips/Focus/Caption/… tabs match the editor dock exactly
            (shape, size, active state, coloured glyphs) instead of a parallel pill
            row. Keeps the tablist semantics + store testids. */}
        <div
          className="store-category-chips tool-dock-row"
          role="tablist"
          aria-label="Store categories"
          data-testid="store-category-chips"
        >
          {STORE_TOOL_ORDER.map((id) => {
            const active = tool === id;
            return (
              <button
                key={id}
                ref={active ? activeTabRef : undefined}
                type="button"
                role="tab"
                aria-selected={active}
                className={`tool-dock-card${active ? " active" : ""}`}
                data-testid={`store-cat-${id}`}
                onClick={() => selectTool(id)}
              >
                <span className="tool-dock-icon" aria-hidden data-tool={id}>
                  {TOOL_ICONS[id]}
                </span>
                <span className="tool-dock-label">{TOOL_LABELS[id]}</span>
              </button>
            );
          })}
        </div>

        <div className="studio-store-body" data-testid="studio-store-body">
          {selected ? (
            <StoreItemDetail
              item={selected}
              cash={props.cash}
              purchases={props.purchases}
              equippedTheme={props.equippedTheme}
              purchasesDisabled={props.purchasesDisabled}
              onBack={() => setSelectedId(null)}
              onPurchase={props.onPurchase}
              onEquipTheme={props.onEquipTheme}
            />
          ) : groups.length === 0 ? (
            <p className="store-empty" data-testid="store-tool-empty">
              Every {TOOL_LABELS[tool]} option is already free. Nothing to buy here yet.
            </p>
          ) : (
            <div className="store-subcats" data-testid="store-subcats">
              {groups.map((group) => (
                <section
                  key={group.sub}
                  className="store-subcat"
                  data-testid={`store-sub-${slug(group.sub)}`}
                >
                  <h3 className="store-subcat-title">{group.sub}</h3>
                  <ul className="store-card-grid">
                    {group.items.map((item) => {
                      const owned = props.purchases.includes(item.id);
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            className={`store-item-card${owned ? " owned" : ""}`}
                            data-testid={`store-card-${item.id}`}
                            onClick={() => setSelectedId(item.id)}
                          >
                            <span className="store-item-card-icon" aria-hidden data-tool={item.tool}>
                              {TOOL_ICONS[item.tool]}
                            </span>
                            <span className="store-item-card-title">{item.title}</span>
                            <span className="store-item-card-price">
                              {owned ? "Owned" : `${item.cost} ${CURRENCY_LABEL}`}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StoreItemDetail(props: {
  item: StoreCatalogItem;
  cash: number;
  purchases: readonly StoreItemId[];
  equippedTheme: EquippedResultCardTheme;
  purchasesDisabled?: boolean;
  onBack: () => void;
  onPurchase: (itemId: StoreItemId) => void;
  onEquipTheme: (theme: EquippedResultCardTheme) => void;
}) {
  const { item } = props;
  const owned = props.purchases.includes(item.id);
  const canBuy = !owned && !props.purchasesDisabled && props.cash >= item.cost;
  const needMore = !owned && props.cash < item.cost ? item.cost - props.cash : 0;
  const contents = grantContentLabels(item, creativeOptionLabel);
  const previewLook =
    item.grants.lookIds?.[0] &&
    SHARED_LOOK_TREATMENTS.find((t) => t.id === item.grants.lookIds![0]);

  return (
    <article className="studio-store-detail" data-testid={`studio-preview-${item.id}`}>
      <button
        type="button"
        className="store-detail-back"
        data-testid="store-detail-back"
        aria-label="Back to list"
        onClick={props.onBack}
      >
        ‹ Back
      </button>
      <div className="store-detail-visual" data-testid="studio-preview-visual" aria-hidden>
        {previewLook ? (
          <span className="look-thumb" style={lookPreviewStyle(previewLook)} />
        ) : (
          <span className="store-detail-icon" data-tool={item.tool}>
            {TOOL_ICONS[item.tool]}
          </span>
        )}
      </div>
      <h3 className="store-detail-title">{item.title}</h3>
      <p className="store-detail-effect" data-testid="store-item-effect">
        {item.effect}
      </p>
      {contents.length > 0 ? (
        <ul className="store-contents" data-testid="store-item-contents">
          {contents.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      <p className="store-detail-price" data-testid="studio-item-price">
        {owned ? "Owned" : `${item.cost} ${CURRENCY_LABEL}`}
      </p>
      {needMore > 0 ? (
        <p className="hint-line" data-testid="studio-insufficient-cash">
          Need {needMore} more {CURRENCY_LABEL}
        </p>
      ) : null}
      <div className="cta-row">
        <button
          type="button"
          className="primary"
          data-testid={`buy-${item.id}`}
          disabled={!canBuy}
          onClick={() => {
            track({ name: "store_item_purchased", props: { itemId: item.id } });
            props.onPurchase(item.id);
          }}
        >
          {owned ? "Owned" : "Buy"}
        </button>
        {item.id === "export-card-theme" && owned ? (
          <button
            type="button"
            data-testid="equip-portfolio-alt"
            aria-pressed={props.equippedTheme === "portfolio-alt"}
            onClick={() =>
              props.onEquipTheme(
                props.equippedTheme === "portfolio-alt" ? "default" : "portfolio-alt",
              )
            }
          >
            {props.equippedTheme === "portfolio-alt" ? "Equipped" : "Equip"}
          </button>
        ) : null}
      </div>
    </article>
  );
}
